// ─── AutoChinBot Global Restore (background running) ───────────
// Auto-starts AutoChinBot engines after login WITHOUT opening the monitor
// page, so the market stream runs continuously (background) and the bot is
// always ready to receive user instructions.
//
// Engines survive tab switches, minimize and page navigation; they only stop
// on explicit off or when the browser closes. Mirrors the Chin Bot restore.
//
// Session key: {strategyId}::account::{accountId}   (per cTrader account)
// session_data.type = 'autochinbot'

import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import {
  getOrCreateAutoChinEngine,
  getAutoChinEngine,
  getAllAutoChinEngines,
} from '@/lib/auto-chin-bot-engine';
import {
  ensureAutoChinTaskRuntime,
  getAllAutoChinTaskRuntimes,
} from '@/lib/autochin-task-runtime';
import {
  setAutoChinSupervisorUser,
  startAutoChinSupervisor,
  stopAutoChinSupervisor,
  upsertRunTarget,
} from '@/lib/autochin-background-supervisor';
import { buildSessionPrefix, parseSessionKey } from '@/lib/session-key';

// ─── Types ──────────────────────────────────────────────────

interface AutoChinStrategyRow {
  id: string;
  name: string;
  active_accounts: string[];
  symbol_list_id: string | null;
}

interface SymbolEntry {
  symbolId: number;
  symbolName: string;
  digits?: number;
}

interface SymbolListRow {
  id: string;
  name: string;
  ctrader_account_id: string | null;
  symbols: SymbolEntry[];
}

interface SessionRow {
  strategy_id: string;
  session_data: Record<string, unknown>;
}

const AUTO_CHIN_TIMEFRAME = 'H1';

// ─── Hook ───────────────────────────────────────────────────

export function useAutoChinGlobalRestore(userId: string | null) {
  const restoredRef = useRef(false);
  const pendingTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearAllTimers = () => {
    pendingTimersRef.current.forEach((t) => clearTimeout(t));
    pendingTimersRef.current = [];
  };

  useEffect(() => {
    // ─── Logout: stop all AutoChinBot engines + supervisor ──────────────
    if (!userId) {
      stopAutoChinSupervisor();
      const engines = getAllAutoChinEngines();
      for (const [, engine] of engines) {
        engine.stop();
      }
      const runtimes = getAllAutoChinTaskRuntimes();
      for (const [key, rt] of runtimes) {
        rt.destroy();
        runtimes.delete(key);
      }
      restoredRef.current = false;
      clearAllTimers();
      return;
    }

    if (restoredRef.current) return;

    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [2000, 4000, 8000, 16000, 30000];

    const scheduleTimer = (fn: () => void, delayMs: number) => {
      const t = setTimeout(fn, delayMs);
      pendingTimersRef.current.push(t);
      return t;
    };

    const querySessionsForStrategy = async (
      stratId: string,
    ): Promise<Map<string, Record<string, unknown>>> => {
      const accountSessions = new Map<string, Record<string, unknown>>();
      try {
        const prefix = buildSessionPrefix(stratId);
        const { data: compositeRows, error: compositeErr } = await supabase
          .from('bot_session_state')
          .select('strategy_id, session_data')
          .eq('user_id', userId)
          .like('strategy_id', prefix);

        if (!compositeErr && compositeRows) {
          for (const row of (compositeRows as unknown as SessionRow[])) {
            const parsed = parseSessionKey(row.strategy_id);
            if (parsed && row.session_data?.type === 'autochinbot') {
              accountSessions.set(parsed.accountId, row.session_data);
            }
          }
        }

        if (accountSessions.size === 0) {
          const { data: legacyRow, error: legacyErr } = await supabase
            .from('bot_session_state')
            .select('session_data')
            .eq('user_id', userId)
            .eq('strategy_id', stratId)
            .maybeSingle();

          if (!legacyErr && legacyRow?.session_data && legacyRow.session_data.type === 'autochinbot') {
            const legacyAccount = (legacyRow.session_data.selectedAccountId as string) || '';
            if (legacyAccount) accountSessions.set(legacyAccount, legacyRow.session_data);
          }
        }
      } catch (err) {
        console.warn('[AutoChinRestore] Session query error:', err);
      }
      return accountSessions;
    };

    const scheduleRetry = (currentAttempt: number) => {
      const nextAttempt = currentAttempt + 1;
      if (nextAttempt < MAX_RETRIES) {
        scheduleTimer(() => tryRestore(nextAttempt), RETRY_DELAYS[currentAttempt]);
      } else {
        console.warn('[AutoChinRestore] Max retries exhausted.');
        restoredRef.current = false;
      }
    };

    const tryRestore = async (attempt: number) => {
      try {
        // Step 1: fetch autochinbot strategies
        const { data: strategies, error } = await supabase
          .from('strategies')
          .select('id, name, active_accounts, symbol_list_id')
          .eq('type', 'autochinbot')
          .eq('user_id', userId);

        if (error) {
          scheduleRetry(attempt);
          return;
        }

        if (!strategies || (strategies as unknown[]).length === 0) {
          restoredRef.current = true; // nothing to restore
          return;
        }

        const rows = strategies as unknown as AutoChinStrategyRow[];

        // Step 2: gateway status
        let gatewayAccounts: Array<{ account_id: string }> = [];
        let gatewayReady = false;
        try {
          const { data: gwData } = await supabase.functions.invoke<{
            connected: boolean;
            accounts: Array<{ account_id: string }>;
          }>('ctrader-gateway', { method: 'POST', body: { action: 'status' } });

          if (gwData?.connected && gwData?.accounts && gwData.accounts.length > 0) {
            gatewayAccounts = gwData.accounts;
            gatewayReady = true;
          }
        } catch {
          // gateway unreachable — retry
        }

        if (!gatewayReady) {
          scheduleRetry(attempt);
          return;
        }

        let startedCount = 0;

        for (const strat of rows) {
          const activeAccounts = Array.isArray(strat.active_accounts) ? strat.active_accounts : [];
          if (activeAccounts.length === 0) continue;

          const accountSessions = await querySessionsForStrategy(strat.id);

          for (const accountToUse of activeAccounts) {
            const accountExists = gatewayAccounts.some((a) => String(a.account_id) === String(accountToUse));
            if (!accountExists) continue;

            const sessionData = accountSessions.get(accountToUse) || null;
            let savedListId: string | null = null;
            if (sessionData) {
              const ws = sessionData.workspace as Record<string, unknown> | undefined;
              if (ws) savedListId = (ws.selectedListId as string) || null;
            }

            const effectiveListId = savedListId || strat.symbol_list_id;
            if (!effectiveListId) continue;

            let symbolList: SymbolListRow | null = null;
            try {
              const { data: listData, error: listErr } = await supabase
                .from('symbol_lists')
                .select('id, name, ctrader_account_id, symbols')
                .eq('id', effectiveListId)
                .maybeSingle();

              if (listErr || !listData) continue;
              symbolList = listData as unknown as SymbolListRow;
            } catch {
              continue;
            }

            if (!symbolList.symbols || symbolList.symbols.length === 0) continue;

            const symbolInfos = symbolList.symbols.map((s) => ({
              symbolId: s.symbolId,
              symbolName: s.symbolName,
              digits: s.digits,
            }));

            // Register this engine as a background run target — the supervisor
            // keeps it alive (self-heal) and guarantees the task pipeline,
            // regardless of whether the monitor is ever opened.
            upsertRunTarget({
              strategyId: strat.id,
              accountId: accountToUse,
              symbols: symbolInfos,
              timeframe: AUTO_CHIN_TIMEFRAME,
            });

            const existingEngine = getAutoChinEngine(strat.id, accountToUse);
            if (existingEngine && existingEngine.isRunning) {
              // Engine already running — still make sure the task pipeline
              // (edata + etask) is attached so tasks never silently die.
              ensureAutoChinTaskRuntime(strat.id, accountToUse, userId);
              startedCount++;
              continue;
            }

            try {
              const engine = getOrCreateAutoChinEngine(strat.id, accountToUse);
              await engine.init(accountToUse, symbolInfos, AUTO_CHIN_TIMEFRAME);
              engine.start();
              // Start the task pipeline (edata + etask) alongside the market
              // engine so task detection also runs in the background.
              ensureAutoChinTaskRuntime(strat.id, accountToUse, userId);
              startedCount++;
              restoredRef.current = true;
              console.log(`[AutoChinRestore] ✅ Started AutoChinBot "${strat.name}" on account ${accountToUse} (${symbolInfos.length} symbols)`);
            } catch (initErr) {
              console.warn(`[AutoChinRestore] Init failed for "${strat.name}" / ${accountToUse}:`, (initErr as Error).message);
            }
          }
        }

        // Hand the target set to the background supervisor so the watchdog
        // keeps every engine + task alive from now until logout/browser close.
        setAutoChinSupervisorUser(userId);
        startAutoChinSupervisor();

        const hasRunnable = rows.some(
          (s) => Array.isArray(s.active_accounts) && s.active_accounts.length > 0 && !!s.symbol_list_id,
        );

        if (startedCount === 0 && hasRunnable) {
          scheduleRetry(attempt);
        }
      } catch (err) {
        console.error('[AutoChinRestore] Unexpected error:', err);
        scheduleRetry(attempt);
      }
    };

    // Small delay so the app shell has time to settle.
    scheduleTimer(() => tryRestore(0), 2000);

    return () => {
      clearAllTimers();
      restoredRef.current = false;
    };
  }, [userId]);
}